import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogOverlay,
  DialogPortal,
  DialogClose,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { ChevronDown, X } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { getAssetWallet } from "../services/digitalWalletService";
import coin from "../assets/images/placeholder/crypto-colour.png";
import AssetList from "./AssetList";

const AssetNetSelector = ({
  onWalletSelect,
  onAssetSelect,
  hotActionAsset,
  setLoadingAsset,
  tradingNets,
  tradingAssets,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState([]);
  const [selectedNetwork, setSelectedNetwork] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const handleSelectNet = useCallback(
    (net) => {
      setLoadingAsset(true);
      setSelectedNetwork(net);
      setOpen(false);
    },
    [setLoadingAsset]
  );

  // Memoize handleSelect to prevent unnecessary recreations
  const handleSelect = useCallback(
    (asset) => {
      setSelectedAsset(asset);
      onAssetSelect?.(asset);
    },
    [onAssetSelect]
  );

  // Effect for fetching wallet data
  useEffect(() => {
    if (!selectedNetwork || !selectedNetwork.assetId || !selectedNetwork._id)
      return;

    const fetchWallet = async () => {
      let actualWallet = [];
      let noWallet = 0;

      try {
        const walletData = {
          assetId: selectedNetwork.assetId,
          chainId: selectedNetwork._id,
        };

        const data = await getAssetWallet(walletData);

        if (data?.data?.data) {
          actualWallet = data.data.data;
          noWallet = 0;

          if (onWalletSelect) {
            onWalletSelect(actualWallet, noWallet, selectedNetwork);
          }
        }
      } catch (err) {
        actualWallet = [];
        noWallet = 1;
        if (onWalletSelect) {
          onWalletSelect(actualWallet, noWallet, selectedNetwork);
        }
      } finally {
        setLoadingAsset(false);
      }
    };

    fetchWallet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedNetwork?.assetId,
    selectedNetwork?._id,
    selectedNetwork,
    setLoadingAsset,
  ]);

  useEffect(() => {
    if (!hotActionAsset) return;

    handleSelect(hotActionAsset);
    const assetId = hotActionAsset?.assetId;
    const matchedNetwork = tradingNets?.find((net) => net.assetId === assetId);

    if (matchedNetwork) {
      handleSelectNet(matchedNetwork);
    }
  }, [
    hotActionAsset,
    tradingNets,
    tradingAssets,
    handleSelect,
    handleSelectNet,
  ]);

  const filteredAssets = tradingAssets?.filter(
    (assets) =>
      assets?.asset?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      assets?.assetName?.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  return (
    <>
      <label className="text-gray-600 text-sm">Select asset</label>
      <Dialog open={open} onOpenChange={setOpen} modal={false}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            className="w-full flex items-center justify-between py-11 rounded-2xl hover:border-brand-primary transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center">
                <img
                  src={
                    selectedAsset && selectedAsset.assetIcon
                      ? selectedAsset.assetIcon
                      : coin
                  }
                  alt={
                    selectedAsset ? selectedAsset.assetName : "Default Asset"
                  }
                  width={40}
                  height={40}
                  className="text-white"
                />
              </div>
              <div className="text-left">
                <div className="font-semibold">
                  {selectedAsset?.asset
                    ? selectedAsset.asset.toUpperCase()
                    : "Select..."}
                </div>
              </div>
            </div>
            <ChevronDown className="text-gray-400" />
          </Button>
        </DialogTrigger>
        <DialogPortal>
          <DialogOverlay className="bg-brand-primary/45  backdrop-blur-[4px] fixed inset-0" />
          <DialogContent
            className="max-w-md rounded-lg max-h-[500px] flex flex-col"
            aria-describedby="saved-addresses-description"
          >
            <DialogClose asChild>
              <Button
                variant="ghost"
                onClick={() => setOpen(false)}
                className="w-10 h-10 absolute top-4 right-4 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
              >
                <X size={18} className="text-gray-700" />
              </Button>
            </DialogClose>
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">
                {"Choose Asset"}
              </DialogTitle>
            </DialogHeader>
            <div id="saved-addresses-description" className="mt-4">
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search addresses..."
                  className="w-full border rounded-lg px-3 py-2"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="overflow-y-auto max-h-[300px] px-2">
                <AssetList
                  ownedAssets={filteredAssets}
                  network={tradingNets}
                  handleSelect={handleSelect}
                  handleSelectNet={handleSelectNet}
                />
              </div>
            </div>
          </DialogContent>
        </DialogPortal>
      </Dialog>
    </>
  );
};

export default AssetNetSelector;
